/**
 * File Manager asset version + import flows.
 *
 * `getCTIDAssets` and `revertFile` use `runAsyncAction` for the standard
 * spinner/error scaffolding. `importFiles` composes two adapter calls (post
 * for new keys, patch for updated keys) and reports partial failures through
 * a distinct toast, so it stays with the manual pattern to preserve that
 * multi-branch messaging.
 */

import getRouterConfig from "@hooks/useRouter";
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  reportError,
  closeDock,
  clearFormFields,
  enableSave,
} from "@actions/global";
import { setCTIDAssets, setFileVersions } from "@features/files/actions";
import { userErrorMessage } from "@utils/custom-errors";
import { runAsyncAction } from "@utils/runAsyncAction";
import { addFilesToRedux } from "./files-crud";
import {
  initialFilesConfig,
  getFileManagerAdapter,
  type Store,
} from "./files-helpers";
import type { FileThunk } from "./files-init";

export const getCTIDAssets = (
  ctid: string | number
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };

  await runAsyncAction(
    dispatch,
    async () => {
      const filesAdapter = await getFileManagerAdapter(store);
      const apiResult = await filesAdapter.fetchCTIDAssets(ctid);
      if (!(apiResult && apiResult.success))
        throw apiResult?.error ?? "";

      const { assets, source_ctid } = apiResult;
      const updatedAssets: Record<string, any> = {};
      updatedAssets.new = assets.new?.map((asset: any) => ({
        url: Object.values(asset)[0],
        status: "new",
        file_id: Object.keys(asset)[0],
      }));
      updatedAssets.updated = assets.updated?.map((asset: any) => ({
        url: Object.values(asset)[0],
        status: "updated",
        file_id: Object.keys(asset)[0],
      }));
      store.dispatch(setCTIDAssets(updatedAssets, source_ctid));
    },
    { errorAction: "fetch", errorSubject: "assets" }
  );
};

/**
 * Runs a mixed import: `new_keys` go through POST, `updated_keys` through
 * PATCH. A per-branch failure list is surfaced via a distinct toast so the
 * user can identify which assets didn't land; this differs from the standard
 * runAsyncAction failure surface, so this thunk keeps the manual pattern.
 */
export const importFiles = (
  _id: any,
  _action: string
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(closeDock());
  store.dispatch(showSpinner());
  const { importAssets } = store.getState();
  const { assetsToImport, import_source_ctid, scheduled_date } = importAssets;
  const filesAdapter = await getFileManagerAdapter(store);

  const new_keys: Record<string, string>[] = [];
  const updated_keys: Record<string, string>[] = [];
  assetsToImport.map((asset: any) =>
    asset.status === "new"
      ? new_keys.push({ [asset.file_id]: asset.url })
      : updated_keys.push({ [asset.file_id]: asset.url })
  );
  const postDetails =
    new_keys.length > 0
      ? {
          source_ctid: import_source_ctid,
          scheduled_date: scheduled_date || null,
          new_keys: new_keys,
        }
      : null;
  const patchDetails =
    updated_keys.length > 0
      ? {
          source_ctid: import_source_ctid,
          scheduled_date: scheduled_date || null,
          updated_keys: updated_keys,
        }
      : null;
  const post_result = postDetails
    ? await filesAdapter.importFiles(postDetails)
    : { success: true };
  const patch_result = patchDetails
    ? await filesAdapter.syncFiles(patchDetails)
    : { success: true };
  if (
    post_result &&
    post_result.success &&
    patch_result &&
    patch_result.success
  ) {
    addFilesToRedux(initialFilesConfig)(dispatch, getState);
    store.dispatch(stopSpinner());
    store.dispatch(clearFormFields());
    // @ts-expect-error - history is possibly null
    const { history } = getRouterConfig();
    const failed_to_add = post_result?.failed_to_import || {};
    const failed_to_update = patch_result?.failed_to_import || {};
    if (
      (failed_to_add && failed_to_add.length) ||
      (failed_to_update && failed_to_update.length)
    ) {
      store.dispatch(
        setToastMessage(
          "Some assets failed to import.",
          history ? history.push("/files") : null
        )
      );
    } else {
      store.dispatch(
        setToastMessage(
          "Assets have been imported.",
          history ? history.push("/files") : null
        )
      );
    }
  } else {
    const errorMessage =
      typeof post_result === "string"
        ? post_result
        : typeof patch_result === "string"
          ? patch_result
          : "";
    store.dispatch(
      reportError(userErrorMessage("import", "assets", errorMessage))
    );
  }
  store.dispatch(stopSpinner());
  store.dispatch(enableSave());
};

export const loadFileVersions = (
  file_location: string
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  const filesAdapter = await getFileManagerAdapter(store);
  const assetsVersions = await filesAdapter.fetchAssetsVersions(file_location);
  const versions = assetsVersions.success ? assetsVersions.versions : [];
  store.dispatch(setFileVersions(versions));
};

/**
 * Reverts an asset to a previous version. Uses a bespoke success/failure
 * toast pattern (both branches are toast dispatches — no alert dock), so it
 * doesn't use runAsyncAction; still, the surrounding spinner/dock/clear
 * scaffolding is unchanged.
 */
export const revertFile = (
  payload: any,
  _action: string
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(closeDock());
  store.dispatch(showSpinner());
  const filesAdapter = await getFileManagerAdapter(store);

  const results = await filesAdapter.revertAssetsVersion(payload);
  store.dispatch(stopSpinner());
  store.dispatch(clearFormFields());
  // @ts-expect-error - history is possibly null
  const { history } = getRouterConfig();
  if (results.success) {
    store.dispatch(
      setToastMessage(
        `File has been reverted to file version ${payload.version_id}.`,
        history ? history.push("/files") : null
      )
    );
  } else {
    store.dispatch(
      setToastMessage(
        `File failed to revert.`,
        history ? history.push("/files") : null
      )
    );
  }
  addFilesToRedux(initialFilesConfig)(dispatch, getState);
};
